import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Images, Search, FileText, ExternalLink, Copy, Check, Plus, X } from "lucide-react";

const DOC_ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const isImageMedia = (m) =>
  (m?.content_type || "").startsWith("image/") ||
  /\.(png|jpe?g|gif|webp|svg|avif|bmp)$/i.test(m?.original_filename || "");

// Same URL and behaviour as « Copier l'URL » in MediaAdmin
const copyUrl = (url) => { navigator.clipboard.writeText(mediaUrl(url)); toast.success("URL copiée"); };

/** Media library picker. Single mode: onPick(m) / onUse(url). Multi mode: onUseMany(urls). */
const MediaLibraryDialog = ({ open, onOpenChange, isDoc = false, multi = false, onPick, onUse, onUseMany }) => {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const { data: media = [], isLoading } = useQuery({
    queryKey: ["admin-media"],
    queryFn: async () => (await api.get("/admin/media")).data,
    enabled: open,
  });
  const relevant = media.filter((m) => (isDoc ? !isImageMedia(m) : isImageMedia(m)));
  const filtered = q ? relevant.filter((m) => (m.original_filename || "").toLowerCase().includes(q.toLowerCase())) : relevant;
  const toggle = (m) => setSelected((s) => (s.includes(m.url) ? s.filter((u) => u !== m.url) : [...s, m.url]));
  const close = (v) => { if (!v) setSelected([]); onOpenChange(v); };
  const addSelected = () => { onUseMany(selected.map(mediaUrl)); close(false); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Bibliothèque Médias — {isDoc ? "documents" : "images"}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-f2b-gray" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom de fichier…" className="pl-9" data-testid="media-library-search" />
          </div>
          {multi && (
            <button type="button" onClick={addSelected} disabled={!selected.length}
              className="shrink-0 px-3 py-2 bg-f2b-cyan text-f2b-black text-sm flex items-center gap-1 disabled:opacity-40" data-testid="media-library-add-selected">
              <Plus size={14} /> Ajouter la sélection ({selected.length})
            </button>
          )}
        </div>
        {isLoading ? (
          <p className="text-f2b-gray text-sm">Chargement…</p>
        ) : filtered.length === 0 ? (
          <p className="text-f2b-gray text-sm">Aucun {isDoc ? "document" : "média"}{q ? " pour cette recherche" : ""}.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="media-library-grid">
            {filtered.map((m) => {
              const on = multi && selected.includes(m.url);
              return (
                <div key={m.id} className={`group border bg-f2b-light p-1 flex flex-col ${on ? "border-f2b-cyan ring-2 ring-f2b-cyan" : "border-f2b-filet hover:border-f2b-cyan"}`}>
                  <button type="button" onClick={() => (multi ? toggle(m) : onPick(m))} className="relative flex flex-col text-left" data-testid={`media-item-${m.id}`}>
                    <div className="aspect-square flex items-center justify-center overflow-hidden bg-white">
                      {isImageMedia(m)
                        ? <img src={mediaUrl(m.url)} alt={m.original_filename || ""} className="max-h-full max-w-full object-contain" />
                        : <FileText size={36} className="text-f2b-gray" />}
                    </div>
                    {on && <span className="absolute top-1 right-1 bg-f2b-cyan text-f2b-black rounded-full p-0.5"><Check size={12} /></span>}
                    <span className="text-[10px] text-f2b-gray truncate mt-1 px-1">{m.original_filename}</span>
                  </button>
                  <div className="flex flex-col gap-1 mt-1">
                    <button type="button" onClick={() => copyUrl(m.url)} className="flex items-center justify-center gap-1 text-[11px] border border-f2b-filet bg-white hover:border-f2b-cyan py-1" data-testid={`media-item-copy-${m.id}`}>
                      <Copy size={12} /> Copier l'URL
                    </button>
                    <button type="button" onClick={() => (multi ? (onUseMany([mediaUrl(m.url)]), close(false)) : onUse(mediaUrl(m.url)))}
                      className="flex items-center justify-center gap-1 text-[11px] bg-f2b-black text-white hover:bg-f2b-cyan py-1" data-testid={`media-item-use-${m.id}`}>
                      <Check size={12} /> {isDoc ? "Utiliser ce document" : "Utiliser cette image"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Image or document field: URL input + upload to object storage + pick from media library. */
const MediaField = ({ value, onChange, testid, kind = "image" }) => {
  const isDoc = kind === "document";
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success(isDoc ? "Document téléversé" : "Image téléversée");
    } catch { toast.error("Échec du téléversement"); }
    setUploading(false);
  };

  const done = () => { setLibOpen(false); toast.success(isDoc ? "Document sélectionné" : "Image sélectionnée"); };
  const pick = (m) => { onChange(m.url); done(); };
  const applyUrl = (url) => { onChange(url); done(); };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={isDoc ? "URL du document, téléversez ou choisissez dans la bibliothèque" : "URL de l'image, téléversez ou choisissez dans la bibliothèque"}
          data-testid={testid} />
        <button type="button" onClick={() => setLibOpen(true)}
          className="shrink-0 px-3 border border-f2b-filet hover:border-f2b-cyan flex items-center gap-2 text-sm text-f2b-gray" data-testid={`${testid}-library`}>
          <Images size={16} /> Bibliothèque
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="shrink-0 px-3 border border-f2b-filet hover:border-f2b-cyan flex items-center gap-2 text-sm text-f2b-gray">
          <Upload size={16} /> {uploading ? "…" : "Upload"}
        </button>
        <input ref={fileRef} type="file" accept={isDoc ? DOC_ACCEPT : "image/*"} className="hidden" onChange={upload} />
      </div>
      {value && (isDoc ? (
        <a href={mediaUrl(value)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-f2b-cyan border border-f2b-filet px-3 py-2 bg-f2b-light">
          <FileText size={16} /> Voir le document <ExternalLink size={12} />
        </a>
      ) : (
        <img src={mediaUrl(value)} alt="aperçu" className="h-20 object-contain border border-f2b-filet bg-f2b-light" />
      ))}
      <MediaLibraryDialog open={libOpen} onOpenChange={setLibOpen} isDoc={isDoc} onPick={pick} onUse={applyUrl} />
    </div>
  );
};

/** Gallery field: list of image URLs, multi-select from library, manual URL entry, remove. */
export const GalleryField = ({ value, onChange, testid }) => {
  const arr = value || [];
  const [libOpen, setLibOpen] = useState(false);
  const addMany = (urls) => {
    onChange([...arr, ...urls]);
    toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} ajoutée${urls.length > 1 ? "s" : ""}`);
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input placeholder="Coller une URL puis Entrée, ou choisir dans la bibliothèque" data-testid={testid}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value.trim()) {
              e.preventDefault();
              onChange([...arr, e.target.value.trim()]);
              e.target.value = "";
            }
          }} />
        <button type="button" onClick={() => setLibOpen(true)}
          className="shrink-0 px-3 border border-f2b-filet hover:border-f2b-cyan flex items-center gap-2 text-sm text-f2b-gray" data-testid={`${testid}-library`}>
          <Images size={16} /> Bibliothèque
        </button>
      </div>
      {arr.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid={`${testid}-list`}>
          {arr.map((u, i) => (
            <div key={`${u}-${i}`} className="relative border border-f2b-filet bg-f2b-light aspect-square flex items-center justify-center overflow-hidden" title={u}>
              <img src={mediaUrl(u)} alt="" className="max-h-full max-w-full object-contain" />
              <button type="button" onClick={() => onChange(arr.filter((_, idx) => idx !== i))}
                className="absolute top-1 right-1 bg-white/90 text-f2b-gray hover:text-red-500 p-0.5" data-testid={`${testid}-remove-${i}`}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <MediaLibraryDialog open={libOpen} onOpenChange={setLibOpen} multi onUseMany={addMany} />
    </div>
  );
};

export default MediaField;
